/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, X, Edit2, ToggleLeft, ToggleRight, Package, AlertCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { materialiAPI } from '../api/materiali';
import { useViewport } from '../hooks/useViewport';

const ACCENT = '#8B5CF6';

export default function Materiali() {
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroAttivo, setFiltroAttivo] = useState('true');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();

  const { data: materiali = [], isLoading } = useQuery({
    queryKey: ['materiali', filtroCategoria, filtroAttivo, search],
    queryFn: () => materialiAPI.list({
      categoria: filtroCategoria || undefined,
      attivo: filtroAttivo || undefined,
      q: search || undefined,
    }),
  });

  const { data: categorie = [] } = useQuery({
    queryKey: ['materiali-categorie'],
    queryFn: () => materialiAPI.categorie(),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, attivo }) => materialiAPI.toggleAttivo(id, attivo),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['materiali'] }),
  });

  // Statistiche header
  const totaleMateriali = materiali.length;
  const scorteBasse = materiali.filter(m =>
    parseFloat(m.quantita_magazzino) <= parseFloat(m.quantita_minima) &&
    parseFloat(m.quantita_minima) > 0 &&
    m.attivo
  ).length;
  const valoreMagazzino = materiali.reduce((acc, m) =>
    acc + (parseFloat(m.prezzo_unitario || 0) * parseFloat(m.quantita_magazzino || 0)), 0
  );

  return (
    <AdminLayout title="Materiali">
      {/* Statistiche */}
      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Totale materiali</div>
          <div style={styles.statValue}>{totaleMateriali}</div>
        </div>
        <div style={{ ...styles.statCard, ...(scorteBasse > 0 ? styles.statCardAlert : {}) }}>
          <div style={styles.statLabel}>Scorte basse</div>
          <div style={{ ...styles.statValue, color: scorteBasse > 0 ? '#DC2626' : '#111827' }}>
            {scorteBasse}
            {scorteBasse > 0 && <AlertCircle size={20} style={{ marginLeft: '0.5rem', verticalAlign: 'middle' }} />}
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Valore magazzino</div>
          <div style={styles.statValue}>€ {valoreMagazzino.toFixed(2)}</div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <div style={styles.filters}>
          <button
            onClick={() => setFiltroCategoria('')}
            style={{ ...styles.filterBtn, ...(filtroCategoria === '' ? styles.filterBtnActive : {}) }}
          >
            Tutte
          </button>
          {categorie.map((c) => (
            <button
              key={c}
              onClick={() => setFiltroCategoria(c)}
              style={{ ...styles.filterBtn, ...(filtroCategoria === c ? styles.filterBtnActive : {}) }}
            >
              {c}
            </button>
          ))}
        </div>
        <button onClick={() => setCreating(true)} style={styles.addBtn}>
          <Plus size={16} /> Nuovo materiale
        </button>
      </div>

      {/* Search + filtro attivo */}
      <div style={styles.searchRow}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Cerca per nome, codice o descrizione..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        <select
          value={filtroAttivo}
          onChange={(e) => setFiltroAttivo(e.target.value)}
          style={styles.select}
        >
          <option value="true">Solo attivi</option>
          <option value="false">Solo disattivati</option>
          <option value="">Tutti</option>
        </select>
      </div>

      {/* Tabella */}
      <div style={styles.tableWrapper}>
        {isLoading && <div style={styles.empty}>Caricamento...</div>}
        {!isLoading && materiali.length === 0 && (
          <div style={styles.empty}>
            <Package size={40} style={{ marginBottom: '0.5rem', color: '#9CA3AF' }} />
            <p>Nessun materiale trovato.</p>
          </div>
        )}
        {!isLoading && materiali.length > 0 && (
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>Codice</th>
                <th style={styles.th}>Nome</th>
                <th style={styles.th}>Categoria</th>
                <th style={styles.th}>UM</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Prezzo</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Scorta</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Valore</th>
                <th style={styles.th}>Stato</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {materiali.map((m) => {
                const scortaBassa = parseFloat(m.quantita_magazzino) <= parseFloat(m.quantita_minima) && parseFloat(m.quantita_minima) > 0;
                const valore = parseFloat(m.prezzo_unitario || 0) * parseFloat(m.quantita_magazzino || 0);
                return (
                  <tr key={m.id} style={styles.tr}>
                    <td style={styles.td}><code style={styles.code}>{m.codice || '—'}</code></td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 600 }}>{m.nome}</div>
                      {m.descrizione && <div style={{ fontSize: '0.78rem', color: '#6B7280', marginTop: '0.15rem' }}>{m.descrizione.substring(0, 50)}{m.descrizione.length > 50 ? '...' : ''}</div>}
                    </td>
                    <td style={styles.td}>{m.categoria || '—'}</td>
                    <td style={styles.td}>{m.unita_misura || '—'}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>
                      € {parseFloat(m.prezzo_unitario || 0).toFixed(2)}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <span style={{ color: scortaBassa ? '#DC2626' : '#111827', fontWeight: scortaBassa ? 600 : 400 }}>
                        {parseFloat(m.quantita_magazzino)}
                      </span>
                      {scortaBassa && (
                        <AlertCircle size={14} style={{ marginLeft: '0.3rem', color: '#DC2626', verticalAlign: 'middle' }} title={`Sotto soglia minima (${m.quantita_minima})`} />
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', color: '#6B7280', fontSize: '0.85rem' }}>
                      € {valore.toFixed(2)}
                    </td>
                    <td style={styles.td}>
                      {m.attivo
                        ? <span style={{ ...styles.statusBadge, backgroundColor: '#D1FAE5', color: '#065F46' }}>Attivo</span>
                        : <span style={{ ...styles.statusBadge, backgroundColor: '#FEE2E2', color: '#991B1B' }}>Disattivato</span>
                      }
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button onClick={() => setEditing(m)} style={styles.iconBtn} title="Modifica">
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => toggleMutation.mutate({ id: m.id, attivo: !m.attivo })}
                          style={styles.iconBtn}
                          title={m.attivo ? 'Disattiva' : 'Attiva'}
                        >
                          {m.attivo ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {(creating || editing) && (
        <MaterialeFormModal
          initial={editing}
          categorie={categorie}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            queryClient.invalidateQueries({ queryKey: ['materiali'] });
            queryClient.invalidateQueries({ queryKey: ['materiali-categorie'] });
          }}
        />
      )}
    </AdminLayout>
  );
}

function MaterialeFormModal({ initial, categorie, onClose, onSaved }) {
  const { isMobile } = useViewport();
  const [form, setForm] = useState({
    codice: initial?.codice || '',
    nome: initial?.nome || '',
    descrizione: initial?.descrizione || '',
    categoria: initial?.categoria || '',
    unita_misura: initial?.unita_misura || 'pz',
    prezzo_unitario: initial?.prezzo_unitario || '',
    quantita_magazzino: initial?.quantita_magazzino || 0,
    quantita_minima: initial?.quantita_minima || 0,
  });
  const [error, setError] = useState('');
  const [nuovaCategoria, setNuovaCategoria] = useState(false);

  const saveMutation = useMutation({
    mutationFn: (data) => initial
      ? materialiAPI.update(initial.id, data)
      : materialiAPI.create(data),
    onSuccess: onSaved,
    onError: (err) => setError(err.response?.data?.error || 'Errore nel salvataggio'),
  });

  const handle = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setError('');
  };

  const submit = (e) => {
    e.preventDefault();
    setError('');
    saveMutation.mutate({
      ...form,
      prezzo_unitario: form.prezzo_unitario === '' ? 0 : parseFloat(form.prezzo_unitario),
      quantita_magazzino: parseFloat(form.quantita_magazzino) || 0,
      quantita_minima: parseFloat(form.quantita_minima) || 0,
    });
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={{ margin: 0 }}>{initial ? 'Modifica materiale' : 'Nuovo materiale'}</h2>
          <button onClick={onClose} style={styles.closeBtn}><X size={20} /></button>
        </div>

        <form onSubmit={submit} style={styles.form}>
          <div style={{ ...styles.row, ...(isMobile ? { flexDirection: 'column' } : {}) }}>
            <div style={{ ...styles.field, flex: 1 }}>
              <label style={styles.label}>Codice</label>
              <input value={form.codice} onChange={handle('codice')} style={styles.input} placeholder="es. TUB-003" />
            </div>
            <div style={{ ...styles.field, flex: 2 }}>
              <label style={styles.label}>Nome *</label>
              <input value={form.nome} onChange={handle('nome')} style={styles.input} required />
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Descrizione</label>
            <textarea value={form.descrizione} onChange={handle('descrizione')} style={{ ...styles.input, minHeight: '60px', fontFamily: 'inherit' }} />
          </div>

          <div style={{ ...styles.row, ...(isMobile ? { flexDirection: 'column' } : {}) }}>
            <div style={styles.field}>
              <label style={styles.label}>Categoria</label>
              {!nuovaCategoria ? (
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <select value={form.categoria} onChange={handle('categoria')} style={{ ...styles.input, flex: 1 }}>
                    <option value="">— Nessuna —</option>
                    {categorie.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button type="button" onClick={() => { setNuovaCategoria(true); setForm({ ...form, categoria: '' }); }} style={styles.smallBtn} title="Nuova categoria">
                    <Plus size={14} />
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <input value={form.categoria} onChange={handle('categoria')} style={{ ...styles.input, flex: 1 }} placeholder="Nome nuova categoria" autoFocus />
                  <button type="button" onClick={() => setNuovaCategoria(false)} style={styles.smallBtn} title="Annulla">
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Unità di misura</label>
              <select value={form.unita_misura} onChange={handle('unita_misura')} style={styles.input}>
                <option value="pz">pz (pezzi)</option>
                <option value="m">m (metri)</option>
                <option value="m2">m² (metri quadri)</option>
                <option value="m3">m³ (metri cubi)</option>
                <option value="kg">kg (chilogrammi)</option>
                <option value="g">g (grammi)</option>
                <option value="l">l (litri)</option>
                <option value="ml">ml (millilitri)</option>
                <option value="conf">conf (confezione)</option>
              </select>
            </div>
          </div>

          <div style={{ ...styles.row, ...(isMobile ? { flexDirection: 'column' } : {}) }}>
            <div style={styles.field}>
              <label style={styles.label}>Prezzo unitario (€)</label>
              <input type="number" step="0.01" min="0" value={form.prezzo_unitario} onChange={handle('prezzo_unitario')} style={styles.input} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Quantità magazzino</label>
              <input type="number" step="0.01" min="0" value={form.quantita_magazzino} onChange={handle('quantita_magazzino')} style={styles.input} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Scorta minima</label>
              <input type="number" step="0.01" min="0" value={form.quantita_minima} onChange={handle('quantita_minima')} style={styles.input} />
              <small style={styles.hint}>0 = nessun alert</small>
            </div>
          </div>

          {error && <div style={styles.errorBox}>{error}</div>}

          <div style={styles.modalActions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>Annulla</button>
            <button type="submit" disabled={saveMutation.isPending} style={styles.submitBtn}>
              {saveMutation.isPending ? 'Salvataggio...' : (initial ? 'Salva modifiche' : 'Crea materiale')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  statCard: { backgroundColor: 'white', padding: '1.25rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  statCardAlert: { borderLeft: '4px solid #DC2626' },
  statLabel: { fontSize: '0.78rem', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 },
  statValue: { fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginTop: '0.4rem' },

  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' },
  filters: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  filterBtn: { padding: '0.45rem 0.9rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 500 },
  filterBtnActive: { backgroundColor: ACCENT, color: 'white', borderColor: ACCENT },
  addBtn: { display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },

  searchRow: { display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' },
  searchWrapper: { position: 'relative', flex: 1, minWidth: '240px' },
  searchIcon: { position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' },
  searchInput: { width: '100%', padding: '0.55rem 0.85rem 0.55rem 2.3rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.92rem', outline: 'none' },
  select: { padding: '0.55rem 0.85rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.92rem', outline: 'none', backgroundColor: 'white', cursor: 'pointer' },

  tableWrapper: { backgroundColor: 'white', borderRadius: '12px', overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '760px' },
  thead: { backgroundColor: '#F9FAFB' },
  th: { padding: '0.85rem 1rem', textAlign: 'left', fontSize: '0.78rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' },
  tr: { borderTop: '1px solid #F3F4F6' },
  td: { padding: '0.85rem 1rem', fontSize: '0.92rem', color: '#374151' },
  code: { backgroundColor: '#F3F4F6', padding: '0.15rem 0.45rem', borderRadius: '4px', fontSize: '0.82rem', fontFamily: 'ui-monospace, monospace' },
  statusBadge: { padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600 },
  iconBtn: { padding: '0.35rem', backgroundColor: 'transparent', border: '1px solid #E5E7EB', borderRadius: '6px', cursor: 'pointer', color: '#374151', display: 'inline-flex' },
  empty: { padding: '3rem', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center' },

  modalOverlay: { position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' },
  modal: { backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto' },
  modalHeader: { padding: '1.25rem 1.5rem', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' },
  form: { padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: 1 },
  row: { display: 'flex', gap: '0.75rem' },
  label: { fontSize: '0.82rem', fontWeight: 600, color: '#374151' },
  input: { padding: '0.55rem 0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', width: '100%' },
  hint: { fontSize: '0.75rem', color: '#6B7280' },
  smallBtn: { padding: '0.5rem 0.7rem', backgroundColor: 'white', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', color: '#374151' },
  errorBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.65rem', borderRadius: '8px', fontSize: '0.85rem' },
  modalActions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' },
  cancelBtn: { padding: '0.55rem 1.1rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 },
  submitBtn: { padding: '0.55rem 1.1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
};
